/**
 * ImportTab — Onglet "Import" de la page Réception POS
 *
 * Extrait de AIParsingDemo.tsx, adapté pour le contexte POS (touch-friendly).
 * Gère l'upload de documents fournisseurs, le polling des jobs,
 * la détection de doublons, et affiche les livraisons récentes.
 */
import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import api, { Delivery } from '../../services/api';
import { useTenant } from '../../contexts/TenantContext';
import { suggestSupplier } from '../../utils/supplierMatch';

type QueueItemStatus = 'uploading' | 'waiting' | 'processing' | 'success' | 'error';

/**
 * Une analyse prend ~30 s. Au-delà de 15 min sans réponse, elle ne viendra plus
 * (tâche perdue au redémarrage du serveur, effacée de la file) : la ligne passe
 * en erreur, avec « Réessayer » et ✕. Avant le 27/10/2026, elle restait
 * « Traitement… » pour toujours — deux documents bloqués 15 jours, sans bouton
 * pour les retirer.
 */
const ANALYSE_PERDUE_MS = 15 * 60 * 1000;
const MESSAGE_PERDUE = 'Analyse perdue par le serveur : réessayez ou retirez le document';

interface QueueItem {
  id: string;
  file: File | { name: string; size: number; type: string };
  status: QueueItemStatus;
  progress?: number;
  jobId?: string;
  result?: {
    delivery_id: string;
    supplier: string;
    document_type: string;
    line_count: number;
    confidence_score: number;
    warning?: string;
  };
  error?: string;
  duplicateDetails?: {
    type: string;
    message: string;
    details: {
      document_number: string;
      supplier: string;
      existing_delivery_id: string;
      status: string;
      status_text: string;
      created_at: string;
      created_date: string;
      created_time: string;
    };
    user_message: string;
  };
  startedAt?: number;
}

interface ImportTabProps {
  onSelectDelivery: (deliveryId: string) => void;
  onActiveJobsCountChange: (count: number) => void;
}

type ImportMode = 'file' | 'email';

export default function ImportTab({
  onSelectDelivery,
  onActiveJobsCountChange,
}: ImportTabProps) {
  const { tenant } = useTenant();
  const [importMode, setImportMode] = useState<ImportMode>('email');
  const [emailText, setEmailText] = useState('');
  const [supplierHint, setSupplierHint] = useState('');
  // Fournisseurs connus (dédupliqués côté API) pour le sélecteur
  const [suppliers, setSuppliers] = useState<string[]>([]);
  const [emailParsing, setEmailParsing] = useState(false);
  const [emailError, setEmailError] = useState<string | null>(null);
  const [emailResult, setEmailResult] = useState<{
    delivery_id: string;
    supplier: string;
    line_count: number;
    confidence_score: number;
  } | null>(null);
  const [queue, setQueue] = useState<QueueItem[]>(() => {
    try {
      const saved = localStorage.getItem('ai-parsing-queue');
      if (saved) {
        const parsed = JSON.parse(saved);
        const tenMinutesAgo = Date.now() - 10 * 60 * 1000;
        return parsed
          .filter((item: QueueItem) => {
            if (item.status === 'success' || item.status === 'error') {
              return (item.startedAt || 0) > tenMinutesAgo;
            }
            return true;
          })
          .map((item: QueueItem) =>
            (item.status === 'waiting' || item.status === 'processing' || item.status === 'uploading') &&
            Date.now() - (item.startedAt || 0) > ANALYSE_PERDUE_MS
              ? { ...item, status: 'error' as QueueItemStatus, error: MESSAGE_PERDUE }
              : item,
          );
      }
    } catch (error) {
      console.error('Failed to restore queue from localStorage:', error);
    }
    return [];
  });
  const [currentTime, setCurrentTime] = useState(Date.now());
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Livraisons récentes
  const [recentDeliveries, setRecentDeliveries] = useState<Delivery[]>([]);
  const [loadingDeliveries, setLoadingDeliveries] = useState(false);

  // Sauvegarder la queue dans localStorage
  useEffect(() => {
    try {
      const serializableQueue = queue.map(item => ({
        ...item,
        file: { name: item.file.name, size: item.file.size, type: item.file.type },
      }));
      localStorage.setItem('ai-parsing-queue', JSON.stringify(serializableQueue));
    } catch (error) {
      console.error('Failed to save queue to localStorage:', error);
    }
  }, [queue]);

  // Timer pour le compteur de temps
  useEffect(() => {
    const interval = setInterval(() => setCurrentTime(Date.now()), 1000);
    return () => clearInterval(interval);
  }, []);

  // Notifier le parent du nombre de jobs actifs
  useEffect(() => {
    const activeCount = queue.filter(i =>
      i.status === 'uploading' || i.status === 'waiting' || i.status === 'processing'
    ).length;
    onActiveJobsCountChange(activeCount);
  }, [queue]);

  // Charger les livraisons récentes
  const loadRecentDeliveries = useCallback(async () => {
    if (!tenant?.id) return;
    try {
      setLoadingDeliveries(true);
      const deliveries = await api.listDeliveries(tenant.id, { limit: 20 });
      setRecentDeliveries(deliveries);
    } catch (err) {
      console.error('Failed to load recent deliveries:', err);
    } finally {
      setLoadingDeliveries(false);
    }
  }, [tenant?.id]);

  useEffect(() => {
    loadRecentDeliveries();
  }, [loadRecentDeliveries]);

  // Charger la liste dédupliquée des fournisseurs (pour le combobox)
  useEffect(() => {
    if (!tenant?.id) return;
    api.getSuppliers(tenant.id).then(setSuppliers).catch(() => { /* best-effort */ });
  }, [tenant?.id]);

  // Upload fichiers
  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const newFiles = Array.from(e.target.files);

      for (const file of newFiles) {
        const queueItemId = `${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;

        setQueue(prev => [...prev, {
          id: queueItemId,
          file,
          status: 'uploading' as QueueItemStatus,
          startedAt: Date.now(),
        }]);

        try {
          const uploadResult = await api.uploadAndParse(file, '');
          setQueue(prev => prev.map(item =>
            item.id === queueItemId
              ? { ...item, status: 'waiting' as QueueItemStatus, jobId: uploadResult.job_id }
              : item
          ));
        } catch (err: any) {
          const errorMessage = err.response?.data?.message || err.message || 'Erreur';
          let duplicateDetails = undefined;
          try {
            const parsed = JSON.parse(errorMessage);
            if (parsed.type === 'DUPLICATE_DELIVERY') duplicateDetails = parsed;
          } catch { /* not JSON */ }

          setQueue(prev => prev.map(item =>
            item.id === queueItemId
              ? { ...item, status: 'error' as QueueItemStatus, error: duplicateDetails ? duplicateDetails.message : errorMessage, duplicateDetails }
              : item
          ));
        }
      }

      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  // Polling des jobs
  useEffect(() => {
    const pollJobStatus = async () => {
      const activeItems = queue.filter(item =>
        (item.status === 'waiting' || item.status === 'processing') && item.jobId
      );
      if (activeItems.length === 0) return;

      for (const item of activeItems) {
        if (Date.now() - (item.startedAt || 0) > ANALYSE_PERDUE_MS) {
          setQueue(prev => prev.map(i =>
            i.id === item.id ? { ...i, status: 'error' as QueueItemStatus, error: MESSAGE_PERDUE } : i
          ));
          continue;
        }
        try {
          const jobStatus = await api.getJobStatus(item.jobId!);
          if (!jobStatus) {
            setQueue(prev => prev.map(i =>
              i.id === item.id ? { ...i, status: 'error' as QueueItemStatus, error: 'Job not found' } : i
            ));
            continue;
          }

          if (jobStatus.state === 'active') {
            setQueue(prev => prev.map(i =>
              i.id === item.id ? { ...i, status: 'processing' as QueueItemStatus, progress: jobStatus.progress } : i
            ));
          } else if (jobStatus.state === 'completed' && jobStatus.result) {
            setQueue(prev => prev.map(i =>
              i.id === item.id
                ? { ...i, status: 'success' as QueueItemStatus, result: jobStatus.result, progress: 100 }
                : i
            ));
            // Refresh recent deliveries on success
            loadRecentDeliveries();
          } else if (jobStatus.state === 'failed') {
            setQueue(prev => prev.map(i =>
              i.id === item.id ? { ...i, status: 'error' as QueueItemStatus, error: jobStatus.error || 'Job failed' } : i
            ));
          }
        } catch (err: any) {
          // Le serveur ne connaît plus la tâche (400 « Job not found ») : elle ne
          // finira jamais. Une autre erreur (réseau) : on réessaie au tour suivant.
          const code = err?.response?.status;
          if (code === 400 || code === 404) {
            setQueue(prev => prev.map(i =>
              i.id === item.id ? { ...i, status: 'error' as QueueItemStatus, error: MESSAGE_PERDUE } : i
            ));
          } else {
            console.error('Error polling job status:', err);
          }
        }
      }
    };

    const interval = setInterval(pollJobStatus, 3000);
    pollJobStatus();
    return () => clearInterval(interval);
  }, [queue, tenant]);

  const removeFromQueue = (id: string) => {
    setQueue(prev => prev.filter(item => item.id !== id));
  };

  const handleDuplicateAndRetry = async (id: string) => {
    const item = queue.find(i => i.id === id);
    if (!item?.duplicateDetails) return;

    const confirmDelete = window.confirm(
      `Supprimer l'ancienne livraison et créer celle-ci ?\n\n` +
      `Document : ${item.duplicateDetails.details.document_number}\n` +
      `Statut : ${item.duplicateDetails.details.status_text}\n` +
      `Créée le : ${item.duplicateDetails.details.created_date}`
    );
    if (!confirmDelete) return;

    try {
      await api.deleteDelivery(tenant?.id || '', item.duplicateDetails.details.existing_delivery_id);
      await retryItem(id);
    } catch (err: any) {
      alert(`Erreur : ${err.response?.data?.message || err.message}`);
    }
  };

  const retryItem = async (id: string) => {
    const item = queue.find(i => i.id === id);
    if (!item) return;
    if (!(item.file instanceof File)) {
      alert('Fichier original non disponible. Veuillez re-uploader.');
      return;
    }

    setQueue(prev => prev.map(i =>
      i.id === id ? { ...i, status: 'uploading' as QueueItemStatus, error: undefined, startedAt: Date.now() } : i
    ));

    try {
      const uploadResult = await api.uploadAndParse(item.file, '');
      setQueue(prev => prev.map(i =>
        i.id === id ? { ...i, status: 'waiting' as QueueItemStatus, jobId: uploadResult.job_id } : i
      ));
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || err.message || 'Erreur';
      setQueue(prev => prev.map(i =>
        i.id === id ? { ...i, status: 'error' as QueueItemStatus, error: errorMessage } : i
      ));
    }
  };

  const clearQueue = () => {
    setQueue([]);
    localStorage.removeItem('ai-parsing-queue');
  };

  // ═══════════════════════════════════════════════════════════
  // Email text parsing
  // ═══════════════════════════════════════════════════════════
  const handleEmailParse = async () => {
    if (!emailText.trim() || emailText.trim().length < 20) {
      setEmailError('Le texte est trop court pour être analysé');
      return;
    }

    setEmailParsing(true);
    setEmailError(null);
    setEmailResult(null);

    try {
      const result = await api.parseEmailText(emailText, supplierHint || undefined);
      setEmailResult(result);
      loadRecentDeliveries();
    } catch (err: any) {
      const message = err.response?.data?.message || err.message || 'Erreur lors du parsing';
      setEmailError(message);
    } finally {
      setEmailParsing(false);
    }
  };

  const resetEmailForm = () => {
    setEmailText('');
    setEmailError(null);
    setEmailResult(null);
    // Garder le fournisseur sélectionné pour le prochain parsing
  };

  // Fournisseurs connus (extraits des livraisons récentes)
  const knownSuppliers = [...new Set(
    recentDeliveries
      .map(d => d.supplier)
      .filter((s): s is string => !!s && s.trim().length > 0)
  )].sort();

  // Liste du menu déroulant = fournisseurs dédupliqués (API) ∪ livraisons récentes
  const supplierOptions = [...new Set([...suppliers, ...knownSuppliers])]
    .sort((a, b) => a.localeCompare(b, 'fr'));

  // Suggestion « fournisseur proche existant » (fautes de frappe) — sans fusion auto.
  const supplierSuggestion = useMemo(
    () => suggestSupplier(supplierHint, supplierOptions),
    [supplierHint, supplierOptions],
  );

  const getConfidenceColor = (score: number) => {
    if (score >= 90) return 'text-green-700 bg-green-100';
    if (score >= 70) return 'text-yellow-700 bg-yellow-100';
    return 'text-red-700 bg-red-100';
  };

  const getStatusIcon = (status: QueueItemStatus) => {
    switch (status) {
      case 'uploading': return '📤';
      case 'waiting': return '⏳';
      case 'processing': return '🔄';
      case 'success': return '✅';
      case 'error': return '❌';
      default: return '📄';
    }
  };

  const getStatusText = (status: QueueItemStatus) => {
    switch (status) {
      case 'uploading': return 'Téléchargement...';
      case 'waiting': return 'En attente';
      case 'processing': return 'Traitement...';
      case 'success': return 'Terminé';
      case 'error': return 'Erreur';
      default: return '';
    }
  };

  const pendingCount = queue.filter(i => i.status === 'uploading' || i.status === 'waiting').length;
  const processingCount = queue.filter(i => i.status === 'processing').length;
  const successCount = queue.filter(i => i.status === 'success').length;
  const errorCount = queue.filter(i => i.status === 'error').length;

  const estimatedMinutesRemaining = (pendingCount + processingCount) * 2.5;
  const formatEstimatedTime = (minutes: number): string => {
    if (minutes < 1) return '< 1 min';
    if (minutes < 60) return `~${Math.ceil(minutes)} min`;
    const hours = Math.floor(minutes / 60);
    const mins = Math.ceil(minutes % 60);
    return `~${hours}h${mins > 0 ? mins + 'min' : ''}`;
  };

  const getDeliveryStatusBadge = (status: string) => {
    switch (status) {
      case 'pending':
        return <span className="px-2 py-1 text-xs font-semibold rounded bg-yellow-100 text-yellow-800">En attente</span>;
      case 'approved':
        return <span className="px-2 py-1 text-xs font-semibold rounded bg-blue-100 text-blue-800">Approuvée</span>;
      case 'applied':
        return <span className="px-2 py-1 text-xs font-semibold rounded bg-green-100 text-green-800">Appliquée</span>;
      default:
        return <span className="px-2 py-1 text-xs font-semibold rounded bg-gray-100 text-gray-800">{status}</span>;
    }
  };

  return (
    <div>
      {/* Toggle mode */}
      <div className="flex bg-gray-100 rounded-lg p-1 mb-4">
        <button
          onClick={() => setImportMode('email')}
          className={`flex-1 py-2.5 text-sm font-semibold rounded-md transition-colors min-h-[48px] ${
            importMode === 'email'
              ? 'bg-white text-green-700 shadow-sm'
              : 'text-gray-600 hover:text-gray-800'
          }`}
        >
          Coller email
        </button>
        <button
          onClick={() => setImportMode('file')}
          className={`flex-1 py-2.5 text-sm font-semibold rounded-md transition-colors min-h-[48px] ${
            importMode === 'file'
              ? 'bg-white text-blue-700 shadow-sm'
              : 'text-gray-600 hover:text-gray-800'
          }`}
        >
          Upload fichier
        </button>
      </div>

      {/* Mode Email */}
      {importMode === 'email' && (
        <div className="bg-white rounded-lg border border-gray-200 p-4 mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Coller le texte de la confirmation de commande
          </label>

          {/* Sélection fournisseur — menu déroulant (orthographe uniformisée) + saisie d'un nouveau */}
          <div className="flex items-center gap-2 mb-3">
            <span className="text-sm text-gray-600 flex-shrink-0">Fournisseur :</span>
            <input
              type="text"
              list="reception-suppliers"
              value={supplierHint}
              onChange={(e) => setSupplierHint(e.target.value)}
              placeholder="Choisir ou saisir un fournisseur…"
              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm min-h-[48px]"
            />
            <datalist id="reception-suppliers">
              {supplierOptions.map(s => (
                <option key={s} value={s} />
              ))}
            </datalist>
            {supplierHint && (
              <button
                type="button"
                onClick={() => setSupplierHint('')}
                title="Effacer"
                className="text-gray-400 hover:text-gray-600 px-2 min-h-[48px]"
              >
                ✕
              </button>
            )}
          </div>

          {/* Suggestion fournisseur proche (fautes de frappe) — l'opérateur confirme */}
          {supplierSuggestion.suggestion && !supplierSuggestion.exact && (
            <div className="mb-3 -mt-1">
              <button
                type="button"
                onClick={() => setSupplierHint(supplierSuggestion.suggestion as string)}
                className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-amber-50 border border-amber-200 text-amber-800 text-sm hover:bg-amber-100"
              >
                💡 Proche de «&nbsp;<strong>{supplierSuggestion.suggestion}</strong>&nbsp;» — cliquer pour utiliser ce fournisseur existant
              </button>
            </div>
          )}

          <textarea
            value={emailText}
            onChange={(e) => setEmailText(e.target.value)}
            placeholder="Collez ici le contenu de l'email de confirmation de commande fournisseur..."
            rows={8}
            className="w-full border border-gray-300 rounded-lg p-3 text-sm resize-y focus:ring-2 focus:ring-green-500 focus:border-green-500"
            disabled={emailParsing}
          />
          <div className="flex items-center justify-between mt-3">
            <p className="text-xs text-gray-500">
              Copier-coller le corps de l'email - Analyse ~3s
            </p>
            <div className="flex gap-2">
              {emailText.trim().length > 0 && (
                <button
                  onClick={resetEmailForm}
                  className="px-4 py-2.5 text-sm text-gray-600 bg-gray-100 rounded-lg font-medium min-h-[48px]"
                  disabled={emailParsing}
                >
                  Effacer
                </button>
              )}
              <button
                onClick={handleEmailParse}
                disabled={emailParsing || emailText.trim().length < 20}
                className="px-6 py-2.5 text-sm text-white bg-green-600 rounded-lg font-semibold hover:bg-green-700 active:bg-green-800 disabled:bg-gray-300 disabled:text-gray-500 min-h-[48px]"
              >
                {emailParsing ? 'Analyse en cours...' : 'Analyser'}
              </button>
            </div>
          </div>

          {/* Erreur email */}
          {emailError && (
            <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-800">
              {emailError}
            </div>
          )}

          {/* Résultat email */}
          {emailResult && (
            <div className="mt-3 bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="grid grid-cols-3 gap-3 text-sm mb-3">
                <div>
                  <span className="text-gray-500 block text-xs">Fournisseur</span>
                  <span className="font-semibold">{emailResult.supplier}</span>
                </div>
                <div>
                  <span className="text-gray-500 block text-xs">Lignes</span>
                  <span className="font-semibold">{emailResult.line_count}</span>
                </div>
                <div>
                  <span className="text-gray-500 block text-xs">Confiance</span>
                  <span className={`inline-block px-1.5 py-0.5 rounded text-xs font-semibold ${getConfidenceColor(emailResult.confidence_score)}`}>
                    {emailResult.confidence_score}%
                  </span>
                </div>
              </div>
              <button
                onClick={() => {
                  onSelectDelivery(emailResult.delivery_id);
                  resetEmailForm();
                }}
                className="w-full py-2.5 bg-green-600 text-white rounded-lg font-semibold text-sm hover:bg-green-700 active:bg-green-800 min-h-[48px]"
              >
                Passer a la reception
              </button>
            </div>
          )}
        </div>
      )}

      {/* Mode Upload fichier (legacy) */}
      {importMode === 'file' && (
        <div className="bg-white rounded-lg border border-gray-200 p-4 mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Selectionner un ou plusieurs documents
          </label>
          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf,.jpg,.jpeg,.png,.webp,.gif,.tiff,.tif,image/*,application/pdf"
            multiple
            onChange={handleFileChange}
            className="block w-full text-sm text-gray-500
              file:mr-4 file:py-3 file:px-6
              file:rounded-lg file:border-0
              file:text-sm file:font-semibold
              file:bg-blue-50 file:text-blue-700
              hover:file:bg-blue-100
              cursor-pointer"
          />
          <p className="text-xs text-gray-500 mt-2">
            PDF, JPEG, PNG, WebP, GIF, TIFF - Max 900 Ko - Traitement ~30s
          </p>
        </div>
      )}

      {/* Stats de la queue (mode fichier uniquement) */}
      {importMode === 'file' && queue.length > 0 && (
        <div className="space-y-2 mb-4">
          <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
            <div className="flex gap-3 text-sm flex-wrap">
              {pendingCount > 0 && <span className="text-yellow-600">⏳ {pendingCount}</span>}
              {processingCount > 0 && <span className="text-blue-600">🔄 {processingCount}</span>}
              {successCount > 0 && <span className="text-green-600">✅ {successCount}</span>}
              {errorCount > 0 && <span className="text-red-600">❌ {errorCount}</span>}
            </div>
            <button onClick={clearQueue} className="text-sm text-red-600 font-medium min-h-[48px] px-2">
              Vider
            </button>
          </div>

          {(pendingCount > 0 || processingCount > 0) && (
            <div className="p-2 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-sm text-blue-800">
                ⏱️ ~{formatEstimatedTime(estimatedMinutesRemaining)} restant
              </p>
            </div>
          )}
        </div>
      )}

      {/* Items de la queue (mode fichier uniquement) */}
      {importMode === 'file' && queue.length > 0 && (
        <div className="space-y-3 mb-6">
          {queue.map((item) => (
            <div
              key={item.id}
              className={`bg-white rounded-lg border-l-4 p-4 ${
                item.status === 'success' ? 'border-green-500' :
                item.status === 'error' ? 'border-red-500' :
                item.status === 'processing' ? 'border-blue-500' :
                item.status === 'uploading' ? 'border-yellow-500' :
                'border-gray-300'
              }`}
            >
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-2 min-w-0">
                  <span className="text-lg">{getStatusIcon(item.status)}</span>
                  <div className="min-w-0">
                    <p className="font-semibold text-sm truncate">{item.file.name}</p>
                    <p className="text-xs text-gray-500">{getStatusText(item.status)}</p>
                  </div>
                </div>
                <div className="flex gap-1 flex-shrink-0">
                  {item.status === 'error' && (
                    <button onClick={() => retryItem(item.id)} className="px-3 py-2 text-xs bg-blue-600 text-white rounded-lg min-h-[48px]">
                      Réessayer
                    </button>
                  )}
                  {item.status !== 'success' && (
                    <button onClick={() => removeFromQueue(item.id)} className="px-2 py-2 text-xs bg-red-100 text-red-700 rounded-lg min-h-[48px]">
                      ✕
                    </button>
                  )}
                </div>
              </div>

              {/* Progress bar */}
              {(item.status === 'waiting' || item.status === 'processing') && (
                <div className="mt-2">
                  <div className="w-full bg-gray-200 rounded-full h-1.5">
                    <div className="bg-blue-600 h-1.5 rounded-full transition-all" style={{ width: `${item.progress || 0}%` }} />
                  </div>
                  {item.startedAt && (
                    <p className="text-xs text-gray-500 mt-1">
                      {Math.floor((currentTime - item.startedAt) / 1000)}s
                    </p>
                  )}
                </div>
              )}

              {/* Doublon */}
              {item.duplicateDetails && (
                <div className="mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <p className="text-sm font-semibold text-yellow-900 mb-1">⚠️ {item.duplicateDetails.message}</p>
                  <p className="text-xs text-yellow-800 mb-2">{item.duplicateDetails.user_message}</p>
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleDuplicateAndRetry(item.id)}
                      className="px-3 py-2 text-xs bg-red-600 text-white rounded-lg min-h-[48px]"
                    >
                      Remplacer l'ancienne
                    </button>
                    <button
                      onClick={() => removeFromQueue(item.id)}
                      className="px-3 py-2 text-xs bg-gray-200 text-gray-700 rounded-lg min-h-[48px]"
                    >
                      Annuler
                    </button>
                  </div>
                </div>
              )}

              {/* Erreur */}
              {item.error && !item.duplicateDetails && (
                <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded text-xs text-red-800">
                  {item.error}
                </div>
              )}

              {/* Succès */}
              {item.result && (
                <div className="mt-3">
                  <div className="grid grid-cols-4 gap-2 text-xs mb-2">
                    <div>
                      <span className="text-gray-500 block">Fournisseur</span>
                      <span className="font-semibold">{item.result.supplier}</span>
                    </div>
                    <div>
                      <span className="text-gray-500 block">Type</span>
                      <span className="font-semibold">{item.result.document_type}</span>
                    </div>
                    <div>
                      <span className="text-gray-500 block">Lignes</span>
                      <span className="font-semibold">{item.result.line_count}</span>
                    </div>
                    <div>
                      <span className="text-gray-500 block">Confiance</span>
                      <span className={`inline-block px-1.5 py-0.5 rounded text-xs font-semibold ${getConfidenceColor(item.result.confidence_score)}`}>
                        {item.result.confidence_score}%
                      </span>
                    </div>
                  </div>
                  <button
                    onClick={() => onSelectDelivery(item.result!.delivery_id)}
                    className="w-full py-2.5 bg-green-600 text-white rounded-lg font-semibold text-sm hover:bg-green-700 active:bg-green-800 min-h-[48px]"
                  >
                    Vérifier cette livraison
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Livraisons récentes */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold text-gray-900">Livraisons récentes</h3>
          <button
            onClick={loadRecentDeliveries}
            className="text-sm text-blue-600 font-medium min-h-[48px] px-2"
          >
            Actualiser
          </button>
        </div>

        {loadingDeliveries ? (
          <div className="text-center py-6 text-gray-500 text-sm">Chargement...</div>
        ) : recentDeliveries.length === 0 ? (
          <div className="text-center py-8 bg-gray-50 rounded-lg">
            <p className="text-gray-600">Aucune livraison</p>
            <p className="text-gray-500 text-sm mt-1">Uploadez un document pour commencer</p>
          </div>
        ) : (
          <div className="space-y-2">
            {recentDeliveries.map((d) => (
              <button
                key={d.id}
                onClick={() => onSelectDelivery(d.id)}
                className="w-full text-left bg-white border border-gray-200 rounded-lg p-3 hover:bg-gray-50 active:bg-gray-100 transition-colors min-h-[56px]"
              >
                <div className="flex items-center justify-between">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 mb-0.5">
                      <span className="font-semibold text-sm truncate">{d.supplier || 'Fournisseur inconnu'}</span>
                      {getDeliveryStatusBadge(d.status)}
                    </div>
                    <div className="flex gap-3 text-xs text-gray-500">
                      <span>{d.document_type}</span>
                      {d.document_number && <span>#{d.document_number}</span>}
                      <span>{new Date(d.created_at).toLocaleDateString('fr-FR')}</span>
                    </div>
                  </div>
                  {d.ai_confidence_score && (
                    <span className={`text-xs font-semibold px-2 py-1 rounded ml-2 ${getConfidenceColor(d.ai_confidence_score)}`}>
                      {d.ai_confidence_score}%
                    </span>
                  )}
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
